import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Flex, Card, Text, Box, Container } from '@radix-ui/themes';
import { useToast } from '@/hooks/use-toast';
import { CheckCircledIcon, CrossCircledIcon } from '@radix-ui/react-icons';

export default function VerifyEmailForm() {
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();

  // Get token from URL parameters
  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setIsVerifying(false);
        setErrorMessage(
          'Verification token is missing. Please check your email link.',
        );
        return;
      }

      try {
        const response = await fetch('/auth/verify-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Email verification failed');
        }

        setIsSuccess(true);
        toast({
          title: 'Email verified successfully!',
          description: 'You can now sign in to your account.',
        });

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (error) {
        setIsSuccess(false);
        const message =
          error instanceof Error
            ? error.message
            : 'An error occurred during verification';
        setErrorMessage(message);
        toast({
          title: 'Verification failed',
          description: message,
          variant: 'destructive',
        });
      } finally {
        setIsVerifying(false);
      }
    };

    verifyEmail();
  }, [token, toast, navigate]);

  // Animated background effect (similar to LoginForm)
  useEffect(() => {
    const canvas = document.getElementById('verify-bg') as HTMLCanvasElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    let time = 0;
    const animate = () => {
      time += 0.005;
      const gradient = ctx.createLinearGradient(
        0,
        0,
        canvas.width,
        canvas.height,
      );

      gradient.addColorStop(
        0,
        `hsl(${220 + Math.sin(time) * 10}, 25%, ${8 + Math.sin(time * 0.3) * 2}%)`,
      );
      gradient.addColorStop(
        0.3,
        `hsl(${240 + Math.cos(time * 0.4) * 8}, 30%, ${12 + Math.cos(time * 0.2) * 3}%)`,
      );
      gradient.addColorStop(
        0.7,
        `hsl(${260 + Math.sin(time * 0.6) * 12}, 35%, ${15 + Math.sin(time * 0.4) * 2}%)`,
      );
      gradient.addColorStop(
        1,
        `hsl(${280 + Math.cos(time * 0.5) * 15}, 40%, ${18 + Math.cos(time * 0.3) * 3}%)`,
      );

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <Box
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        padding: '20px',
      }}
    >
      {/* Dark financial background */}
      <canvas
        id="verify-bg"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1,
        }}
      />

      {/* Subtle grid overlay */}
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `
            linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          zIndex: -1,
        }}
      />

      {/* Glow effects */}
      <Box
        style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '300px',
          height: '300px',
          background: isSuccess
            ? 'radial-gradient(circle, rgba(0, 255, 127, 0.05) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(139, 92, 246, 0.03) 0%, transparent 70%)',
          borderRadius: '50%',
          zIndex: -1,
        }}
      />

      <Container
        size="3"
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: '480px',
        }}
      >
        <Card
          style={{
            width: '100%',
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow:
              '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)',
            borderRadius: '20px',
            transition: 'all 0.3s ease',
          }}
        >
          <Flex direction="column" gap="6" p="8" align="center">
            {/* Status Icon */}
            <Box
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '18px',
                background: isVerifying
                  ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                  : isSuccess
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px',
                color: 'white',
                boxShadow: isSuccess
                  ? '0 12px 40px rgba(16, 185, 129, 0.4)'
                  : isVerifying
                    ? '0 12px 40px rgba(139, 92, 246, 0.4)'
                    : '0 12px 40px rgba(239, 68, 68, 0.4)',
                transition: 'all 0.3s ease',
              }}
            >
              {isVerifying ? (
                <Box
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid rgba(255, 255, 255, 0.3)',
                    borderTop: '3px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }}
                />
              ) : isSuccess ? (
                <CheckCircledIcon style={{ width: 40, height: 40 }} />
              ) : (
                <CrossCircledIcon style={{ width: 40, height: 40 }} />
              )}
            </Box>

            {/* Status Text */}
            <Flex direction="column" gap="3" align="center">
              <Text
                size="7"
                weight="bold"
                align="center"
                style={{
                  background: isSuccess
                    ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    : isVerifying
                      ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {isVerifying
                  ? 'Verifying Email...'
                  : isSuccess
                    ? 'Email Verified!'
                    : 'Verification Failed'}
              </Text>

              <Text
                size="3"
                style={{
                  color: '#e2e8f0',
                  textAlign: 'center',
                  maxWidth: '320px',
                }}
              >
                {isVerifying
                  ? 'Please wait while we verify your email address.'
                  : isSuccess
                    ? 'Your email has been successfully verified. Redirecting you to login...'
                    : errorMessage ||
                      'We could not verify your email. Please try again or contact support.'}
              </Text>
            </Flex>

            {/* Action Buttons */}
            {!isVerifying && (
              <Flex direction="column" gap="3" style={{ width: '100%' }}>
                {isSuccess ? (
                  <Button
                    size="4"
                    onClick={() => navigate('/login')}
                    style={{
                      width: '100%',
                      height: '56px',
                      borderRadius: '12px',
                      background:
                        'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      border: 'none',
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#f8fafc',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 12px 40px rgba(16, 185, 129, 0.4)',
                    }}
                  >
                    Go to Login
                  </Button>
                ) : (
                  <>
                    <Button
                      size="4"
                      onClick={() => navigate('/signup')}
                      style={{
                        width: '100%',
                        height: '56px',
                        borderRadius: '12px',
                        background:
                          'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        border: 'none',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#f8fafc',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 12px 40px rgba(139, 92, 246, 0.4)',
                      }}
                    >
                      Back to Sign Up
                    </Button>
                    <Button
                      size="4"
                      variant="ghost"
                      onClick={() => navigate('/login')}
                      style={{
                        width: '100%',
                        height: '56px',
                        borderRadius: '12px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#e2e8f0',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      Go to Login
                    </Button>
                  </>
                )}
              </Flex>
            )}
          </Flex>
        </Card>
      </Container>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
}
